import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";

const Section = ({ title, children }) => (
  <section className="mt-8">
    <h2 className="font-display text-xl font-bold text-foreground">{title}</h2>
    <div className="mt-3 space-y-3 text-sm leading-7 text-muted-foreground">{children}</div>
  </section>
);

export default function TermsOfService() {
  useEffect(() => { document.title = "Terms of Service | QR Code Creator Studio"; }, []);

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-14">
        <p className="text-sm font-semibold text-primary">LEGAL</p>
        <h1 className="mt-2 font-display text-4xl font-extrabold tracking-tight">Terms of Service</h1>
        <p className="mt-3 text-sm text-muted-foreground">Last updated: 24 September 2026</p>

        <p className="mt-8 text-base leading-7 text-muted-foreground">
          These Terms of Service govern your use of QR Code Creator Studio and qrcodecreatorstudio.com. By creating an account or using the service, you agree to these terms.
        </p>

        <Section title="1. The service">
          <p>QR Code Creator Studio provides tools to create, customise, manage, and analyse QR codes. Features may include static QR codes, dynamic QR codes, scan analytics, team management, file storage, and account administration.</p>
        </Section>

        <Section title="2. Accounts and security">
          <p>You are responsible for providing accurate account information and keeping access to your account secure. Do not share your login credentials or use another person’s account without permission. Tell us promptly if you believe your account has been accessed without authorisation.</p>
        </Section>

        <Section title="3. Your QR code content">
          <p>You are responsible for the destinations, content, files, and information connected to your QR codes. You must have the necessary rights and permissions to use that content.</p>
          <p>You may not use the service for unlawful, fraudulent, harmful, misleading, defamatory, infringing, phishing, malware, or spam-related activity. We may suspend or remove content or accounts that violate these terms or put users, the platform, or third parties at risk.</p>
        </Section>

        <Section title="4. Plans, billing, and dynamic QR codes">
          <p>Some features may require a paid plan. Pricing, billing intervals, renewal information, and payment methods are shown before payment is completed. If a paid plan expires or is cancelled, access to paid features, including dynamic QR functionality or analytics, may be limited or suspended as described in the applicable plan.</p>
        </Section>

        <Section title="5. Availability and changes">
          <p>We aim to keep the service available and reliable, but we cannot guarantee uninterrupted operation. We may update, improve, limit, or discontinue features when reasonably necessary for security, maintenance, legal compliance, or service improvement.</p>
        </Section>

        <Section title="6. Intellectual property">
          <p>QR Code Creator Studio and its software, design, branding, and content are protected by applicable intellectual property laws. You retain ownership of content you provide to the service. You give us permission to process that content only as needed to operate and provide the service.</p>
        </Section>

        <Section title="7. Disclaimers and limitation of liability">
          <p>The service is provided on an “as available” basis. To the maximum extent permitted by law, QR Code Creator Studio is not liable for indirect, incidental, special, consequential, or loss-of-profit damages arising from use of, or inability to use, the service. Nothing in these terms limits liability that cannot legally be limited.</p>
        </Section>

        <Section title="8. Termination">
          <p>You may stop using the service at any time. We may suspend or terminate access if you violate these terms, create a security risk, or misuse the service. Provisions that should reasonably continue after termination will remain in effect.</p>
        </Section>

        <Section title="9. Changes to these terms">
          <p>We may update these terms from time to time. The latest version will be posted on this page with its updated date. Continued use after an update means you accept the revised terms.</p>
        </Section>

        <Section title="10. Contact">
          <p>For questions about these terms, contact us at <a className="text-primary font-semibold" href="mailto:qrcodecreatorstudio@gmail.com">qrcodecreatorstudio@gmail.com</a>.</p>
        </Section>
      </main>

      <PublicFooter />
    </div>
  );
}
